import { useMemo, useState } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { reportsApi, restaurantApi } from '../services/api';
import {
  ComparePeriodBar,
  CoverageBanner,
  Delta,
  GatedCompareRow,
  NoData,
  rangeLabel,
  storedRestaurants,
  type ComparePair,
  type DetailLine,
} from '../components/PeriodCompare';
import { GroupedBars, PairedBars, barColors } from '../components/analytics/Bars';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  PieChart,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

type Period = 'today' | 'week' | 'month';

interface CategoryVariance {
  category: string;
  theoreticalCents: number;
  actualCents: number;
  varianceCents: number;
}

interface TopVarianceItem {
  inventoryItemId: string;
  name: string;
  category: string;
  unit: string;
  theoreticalUsage: number;
  actualUsage: number;
  varianceQuantity: number;
  varianceCents: number;
  wasteQuantity: number;
  possibleCauses: string[];
}

interface FoodCostReport {
  period: { start: string; end: string };
  revenueCents: number;
  purchasesCents: number;
  theoreticalCostCents: number;
  actualCostCents: number;
  varianceCents: number;
  theoreticalFoodCostPercent: number | null;
  actualFoodCostPercent: number | null;
  variancePercent: number | null;
  byCategory: CategoryVariance[];
  topVarianceItems: TopVarianceItem[];
  sourceCounts?: { inventoryTransactions: number; checks: number };
}

// "No data" means no underlying records at all — distinct from a real zero.
const hasNoData = (r: FoodCostReport | null | undefined) =>
  !!r?.sourceCounts && r.sourceCounts.inventoryTransactions === 0 && r.sourceCounts.checks === 0;

// Per-metric coverage from PR #19's sourceCounts { inventoryTransactions,
// checks }. Revenue comes from paid checks; cost + usage come from inventory
// transactions; a food-cost PERCENT needs both (cost over revenue), so it is
// only honest when both families cover the period.
const hasRevenue = (r: FoodCostReport | null | undefined) => (r?.sourceCounts?.checks ?? 0) > 0;
const hasCost = (r: FoodCostReport | null | undefined) => (r?.sourceCounts?.inventoryTransactions ?? 0) > 0;

// The few largest actual-cost categories for a period, "label $amount".
function topCostLabel(r: FoodCostReport | null | undefined, n = 3): string {
  const rows = (r?.byCategory || []).filter((c) => c.actualCents !== 0).sort((a, b) => b.actualCents - a.actualCents).slice(0, n);
  if (rows.length === 0) return 'no categories';
  return rows.map((c) => `${c.category} ${formatCurrency(c.actualCents)}`).join(', ');
}

const fetchFoodCost = async (params: Parameters<typeof reportsApi.getFoodCost>[0]) => {
  const response = await reportsApi.getFoodCost(params);
  const result = response.data?.data || response.data;
  return (result || null) as FoodCostReport | null;
};

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const formatPercent = (value: number | null) =>
  value === null || value === undefined ? '—' : `${value.toFixed(1)}%`;

export default function FoodCostPage() {
  const [period, setPeriod] = useState<Period>('week');
  const [pair, setPair] = useState<ComparePair | null>(null);
  const stores = useMemo(storedRestaurants, []);
  const isMultiStore = stores.length > 1;

  const { data, isLoading } = useQuery({
    queryKey: ['food-cost-report', period],
    queryFn: () => fetchFoodCost({ period }),
  });

  // Period comparison: two explicit ranges, each fetched from stored data.
  const { data: sideA } = useQuery({
    queryKey: ['food-cost-compare', 'a', pair],
    enabled: !!pair,
    queryFn: () => fetchFoodCost({ startDate: pair!.a.start, endDate: pair!.a.end }),
  });
  const { data: sideB } = useQuery({
    queryKey: ['food-cost-compare', 'b', pair],
    enabled: !!pair,
    queryFn: () => fetchFoodCost({ startDate: pair!.b.start, endDate: pair!.b.end }),
  });

  // Store comparison: the same report per sibling store, side by side.
  const storeParamsA = pair ? { startDate: pair.a.start, endDate: pair.a.end } : { period };
  const storeReportsA = useQueries({
    queries: (isMultiStore ? stores : []).map((s) => ({
      queryKey: ['food-cost-store', 'a', s.id, storeParamsA],
      queryFn: () => fetchFoodCost({ ...storeParamsA, restaurantId: s.id }),
    })),
  });
  const storeReportsB = useQueries({
    queries: (isMultiStore && pair ? stores : []).map((s) => ({
      queryKey: ['food-cost-store', 'b', s.id, pair],
      queryFn: () => fetchFoodCost({ startDate: pair!.b.start, endDate: pair!.b.end, restaurantId: s.id }),
    })),
  });

  // Food cost compares POS sales against purchases — a register-side lens.
  // Brands and makers have no register; their cost story lives in Margins
  // (wholesale orders × recipes) and on each recipe. Say so instead of
  // rendering an empty report.
  const { data: restaurant } = useQuery({
    queryKey: ['food-cost-business-type'],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data,
    staleTime: 5 * 60 * 1000,
  });
  let settings: any = restaurant?.settings ?? {};
  while (typeof settings === 'string') { try { settings = JSON.parse(settings); } catch { settings = {}; break; } }
  const isMakerBiz = settings?.businessType === 'cpg' || settings?.businessType === 'bakery';
  const reportEmpty = !data || ((data.theoreticalCostCents ?? 0) === 0 && (data.byCategory || []).length === 0);
  if (!isLoading && isMakerBiz && reportEmpty) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <ProcurementNav />
        <div className="card p-8 text-center">
          <PieChart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-700">Food cost is a register-side report</p>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            It compares point-of-sale sales against purchases. For a brand, the same story
            lives in <Link to="/margins" className="text-primary-600 underline">Margins</Link> —
            wholesale orders × recipe costs — and on each recipe's cost breakdown.
          </p>
        </div>
      </div>
    );
  }

  const report = data;
  const byCategory = report?.byCategory || [];
  const topVarianceItems = report?.topVarianceItems || [];

  // Positive varianceCents = actual cost exceeds theoretical = unfavorable (margin leak)
  const varianceIsUnfavorable = (report?.varianceCents ?? 0) > 0;

  const chartData = byCategory.map((c) => ({
    category: c.category,
    theoretical: c.theoreticalCents / 100,
    actual: c.actualCents / 100,
  }));

  // By-store chart rows — theoretical vs actual food cost % per store, only
  // for stores whose report loaded with real data. The table beneath keeps
  // revenue, variance, and the comparison deltas.
  const storeChartRows = stores
    .map((s, i) => ({ name: s.name, r: storeReportsA[i]?.data }))
    .filter((x) => x.r && !hasNoData(x.r) && x.r.actualFoodCostPercent !== null)
    .map((x) => ({
      store: x.name,
      theoretical: x.r!.theoreticalFoodCostPercent,
      actual: x.r!.actualFoodCostPercent,
    }));

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Food Cost</h1>
            <p className="text-sm md:text-base text-slate-500">
              Theoretical vs actual cost and where margin leaks
            </p>
          </div>
          <HelpButton featureId="reports" />
        </div>
        {/* Period selector */}
        <div className="flex gap-2">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={clsx(
                'flex-1 sm:flex-none px-4 py-2 rounded-lg font-medium transition-colors text-sm',
                period === opt.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Info note */}
      <div className="card p-4 bg-primary-50 border-primary-200">
        <div className="flex items-start gap-3">
          <PieChart className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-800">Theoretical</span> = what recipes say you
            should have used for sales.{' '}
            <span className="font-medium text-slate-800">Actual</span> = what actually left inventory
            (incl. waste &amp; over-portioning). The gap is where margin leaks.
          </p>
        </div>
      </div>

      {/* Period comparison — two real stored ranges, deltas only when both have data */}
      <ComparePeriodBar onChange={setPair} />
      {pair && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">Period comparison</h2>
            <span className="text-sm text-slate-500">
              {rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}
            </span>
          </div>
          {!sideA || !sideB ? (
            <p className="text-sm text-slate-500 p-6">Loading both periods…</p>
          ) : hasNoData(sideA) || hasNoData(sideB) ? (
            <div className="p-6 space-y-1">
              {hasNoData(sideA) && <p><NoData range={pair.a} /></p>}
              {hasNoData(sideB) && <p><NoData range={pair.b} /></p>}
              <p className="text-xs text-slate-400 pt-1">
                Deltas only show when both periods have recorded data — nothing is estimated or backfilled.
              </p>
            </div>
          ) : (
            (() => {
              // Per-metric coverage from PR #19 sourceCounts. Revenue rides on
              // paid checks; cost/usage ride on inventory transactions; a food-
              // cost percent needs both (cost over revenue).
              const aRev = hasRevenue(sideA), bRev = hasRevenue(sideB);
              const aCost = hasCost(sideA), bCost = hasCost(sideB);
              const aPct = aRev && aCost, bPct = bRev && bCost;
              const barOrNull = (ok: boolean, v: number | null) => (ok ? v : null);
              const revDetail: DetailLine[] = [{
                label: 'Paid/closed checks counted',
                a: `${sideA.sourceCounts?.checks ?? 0} checks`,
                b: `${sideB.sourceCounts?.checks ?? 0} checks`,
              }];
              const costDetail: DetailLine[] = [
                {
                  label: 'Inventory transactions counted',
                  a: `${sideA.sourceCounts?.inventoryTransactions ?? 0} txns`,
                  b: `${sideB.sourceCounts?.inventoryTransactions ?? 0} txns`,
                },
                { label: 'Largest categories (actual)', a: topCostLabel(sideA), b: topCostLabel(sideB) },
              ];
              return (
                <>
                  <CoverageBanner
                    a={{ sales: sideA.sourceCounts?.checks ?? 0, expense: sideA.sourceCounts?.inventoryTransactions ?? 0 }}
                    b={{ sales: sideB.sourceCounts?.checks ?? 0, expense: sideB.sourceCounts?.inventoryTransactions ?? 0 }}
                    aRange={pair.a}
                    bRange={pair.b}
                    salesNoun="sales"
                    expenseNoun="inventory"
                  />
                  <div className="p-4">
                    <PairedBars
                      aLabel={rangeLabel(pair.a)}
                      bLabel={rangeLabel(pair.b)}
                      metrics={[
                        { label: 'Revenue', a: barOrNull(aRev, sideA.revenueCents), b: barOrNull(bRev, sideB.revenueCents), kind: 'cents', goodWhenUp: true },
                        { label: 'Actual food cost %', a: barOrNull(aPct, sideA.actualFoodCostPercent), b: barOrNull(bPct, sideB.actualFoodCostPercent), kind: 'points', goodWhenUp: false },
                        { label: 'Actual cost', a: barOrNull(aCost, sideA.actualCostCents), b: barOrNull(bCost, sideB.actualCostCents), kind: 'cents', goodWhenUp: false },
                        { label: 'Variance', a: barOrNull(aCost, sideA.varianceCents), b: barOrNull(bCost, sideB.varianceCents), kind: 'cents', goodWhenUp: false },
                      ]}
                    />
                  </div>
                  <p className="text-xs text-slate-400 px-4 -mt-2 pb-2">Select a metric to see the records behind it.</p>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="table-header">Metric</th>
                          <th className="table-header text-right">{rangeLabel(pair.a)}</th>
                          <th className="table-header text-right">{rangeLabel(pair.b)}</th>
                          <th className="table-header text-right">Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <GatedCompareRow label="Revenue" a={sideA.revenueCents} b={sideB.revenueCents} kind="cents" goodWhenUp
                          aHasData={aRev} bHasData={bRev} noun="sales" aRange={pair.a} bRange={pair.b} detail={revDetail} />
                        <GatedCompareRow label="Actual food cost %" a={sideA.actualFoodCostPercent} b={sideB.actualFoodCostPercent} kind="points" goodWhenUp={false}
                          aHasData={aPct} bHasData={bPct} noun="sales and inventory together" aRange={pair.a} bRange={pair.b} />
                        <GatedCompareRow label="Theoretical food cost %" a={sideA.theoreticalFoodCostPercent} b={sideB.theoreticalFoodCostPercent} kind="points" goodWhenUp={false}
                          aHasData={aPct} bHasData={bPct} noun="sales and inventory together" aRange={pair.a} bRange={pair.b} />
                        <GatedCompareRow label="Actual cost" a={sideA.actualCostCents} b={sideB.actualCostCents} kind="cents" goodWhenUp={false}
                          aHasData={aCost} bHasData={bCost} noun="inventory activity" aRange={pair.a} bRange={pair.b} detail={costDetail} />
                        <GatedCompareRow label="Variance" a={sideA.varianceCents} b={sideB.varianceCents} kind="cents" goodWhenUp={false}
                          aHasData={aCost} bHasData={bCost} noun="inventory activity" aRange={pair.a} bRange={pair.b} />
                        <GatedCompareRow label="Purchases" a={sideA.purchasesCents} b={sideB.purchasesCents} kind="cents" goodWhenUp={false}
                          aHasData={aCost} bHasData={bCost} noun="inventory activity" aRange={pair.a} bRange={pair.b} />
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()
          )}
        </div>
      )}

      {/* Store comparison — the same report per store, side by side */}
      {isMultiStore && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">By store</h2>
            <span className="text-sm text-slate-500">
              {pair ? (
                <>{rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}</>
              ) : (
                PERIOD_OPTIONS.find((o) => o.value === period)?.label
              )}
            </span>
          </div>
          {storeChartRows.length > 0 && (
            <div className="p-4 pb-0">
              <GroupedBars
                data={storeChartRows}
                xKey="store"
                series={[
                  { key: 'theoretical', label: 'Theoretical food cost %', color: barColors.prior },
                  { key: 'actual', label: 'Actual food cost %', color: barColors.current },
                ]}
                format={(v) => `${v.toFixed(1)}%`}
                axisFormat={(v) => `${v}%`}
                height={220}
              />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="table-header">Store</th>
                  <th className="table-header text-right">Revenue</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                  <th className="table-header text-right">Actual food cost %</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                  <th className="table-header text-right">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stores.map((s, i) => {
                  const a = storeReportsA[i]?.data;
                  const b = pair ? storeReportsB[i]?.data : null;
                  if (!a) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right text-slate-400" colSpan={pair ? 5 : 3}>Loading…</td>
                      </tr>
                    );
                  }
                  if (hasNoData(a)) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right" colSpan={pair ? 5 : 3}><NoData range={pair?.a} /></td>
                      </tr>
                    );
                  }
                  const bOk = b && !hasNoData(b) ? b : null;
                  return (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{s.name}</td>
                      <td className="table-cell text-right tabular-nums">{formatCurrency(a.revenueCents)}</td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.revenueCents} prev={bOk.revenueCents} kind="cents" goodWhenUp />
                          ) : b && hasNoData(b) ? (
                            <NoData range={pair.b} />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell text-right tabular-nums">{formatPercent(a.actualFoodCostPercent)}</td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.actualFoodCostPercent} prev={bOk.actualFoodCostPercent} kind="points" goodWhenUp={false} />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell text-right tabular-nums">{formatCurrency(a.varianceCents)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : !report ? (
        <div className="text-center py-12 text-slate-500">
          <PieChart className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No food cost data available for this period</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            {/* Revenue */}
            <div className="card p-4 flex-shrink-0 min-w-[150px] md:min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-xs md:text-sm text-slate-500">Revenue</p>
                <DollarSign className="w-4 h-4 text-primary-600" />
              </div>
              <p className="text-xl md:text-2xl font-bold text-slate-900 mt-1">
                {formatCurrency(report.revenueCents)}
              </p>
            </div>

            {/* Actual Food Cost % (large) */}
            <div className="card p-4 flex-shrink-0 min-w-[150px] md:min-w-0">
              <p className="text-xs md:text-sm text-slate-500">Actual Food Cost</p>
              <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">
                {formatPercent(report.actualFoodCostPercent)}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {formatCurrency(report.actualCostCents)}
              </p>
            </div>

            {/* Theoretical Food Cost % */}
            <div className="card p-4 flex-shrink-0 min-w-[150px] md:min-w-0">
              <p className="text-xs md:text-sm text-slate-500">Theoretical Food Cost</p>
              <p className="text-2xl md:text-3xl font-bold text-slate-700 mt-1">
                {formatPercent(report.theoreticalFoodCostPercent)}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {formatCurrency(report.theoreticalCostCents)}
              </p>
            </div>

            {/* Variance */}
            <div className="card p-4 flex-shrink-0 min-w-[150px] md:min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-xs md:text-sm text-slate-500">Variance</p>
                {varianceIsUnfavorable ? (
                  <TrendingUp className="w-4 h-4 text-danger-600" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-success-600" />
                )}
              </div>
              <p
                className={clsx(
                  'text-xl md:text-2xl font-bold mt-1',
                  varianceIsUnfavorable ? 'text-danger-600' : 'text-success-600'
                )}
              >
                {formatPercent(report.variancePercent)}
              </p>
              <p
                className={clsx(
                  'text-xs mt-0.5',
                  varianceIsUnfavorable ? 'text-danger-500' : 'text-success-500'
                )}
              >
                {varianceIsUnfavorable ? '+' : ''}
                {formatCurrency(report.varianceCents)}
              </p>
            </div>

            {/* Purchases */}
            <div className="card p-4 flex-shrink-0 min-w-[150px] md:min-w-0">
              <p className="text-xs md:text-sm text-slate-500">Purchases</p>
              <p className="text-xl md:text-2xl font-bold text-slate-900 mt-1">
                {formatCurrency(report.purchasesCents)}
              </p>
            </div>
          </div>

          {/* Top Variance Drivers */}
          <div className="card overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning-500" />
              <h2 className="text-lg font-semibold">Top Variance Drivers</h2>
            </div>
            {topVarianceItems.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">
                No significant variance to report
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="table-header">Item</th>
                      <th className="table-header">Category</th>
                      <th className="table-header text-right">Theoretical</th>
                      <th className="table-header text-right">Actual</th>
                      <th className="table-header text-right">Variance</th>
                      <th className="table-header text-right">Waste</th>
                      <th className="table-header">Likely Cause</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topVarianceItems.map((item) => {
                      const unfavorable = item.varianceCents > 0;
                      return (
                        <tr key={item.inventoryItemId} className="hover:bg-slate-50">
                          <td className="table-cell font-medium">{item.name}</td>
                          <td className="table-cell text-slate-600">{item.category}</td>
                          <td className="table-cell text-right text-slate-600">
                            {item.theoreticalUsage} {item.unit}
                          </td>
                          <td className="table-cell text-right text-slate-600">
                            {item.actualUsage} {item.unit}
                          </td>
                          <td
                            className={clsx(
                              'table-cell text-right font-semibold',
                              unfavorable ? 'text-danger-600' : 'text-success-600'
                            )}
                          >
                            {unfavorable ? '+' : ''}
                            {formatCurrency(item.varianceCents)}
                          </td>
                          <td className="table-cell text-right text-slate-600">
                            {item.wasteQuantity} {item.unit}
                          </td>
                          <td className="table-cell text-sm text-slate-500">
                            {item.possibleCauses?.[0] || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* By Category */}
          <div className="card p-4 md:p-6">
            <h2 className="text-lg font-semibold mb-4">By Category</h2>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">
                No category data for this period
              </p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="category"
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      tickFormatter={(value: number) => `$${value.toFixed(0)}`}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `$${value.toFixed(2)}`,
                        name === 'theoretical' ? 'Theoretical' : 'Actual',
                      ]}
                      labelStyle={{ color: '#1e293b' }}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend
                      formatter={(value: string) =>
                        value === 'theoretical' ? 'Theoretical' : 'Actual'
                      }
                    />
                    <Bar dataKey="theoretical" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="actual" fill="#204869" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>

                {/* Category table */}
                <div className="overflow-x-auto mt-4">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="table-header">Category</th>
                        <th className="table-header text-right">Theoretical</th>
                        <th className="table-header text-right">Actual</th>
                        <th className="table-header text-right">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {byCategory.map((cat) => {
                        const unfavorable = cat.varianceCents > 0;
                        return (
                          <tr key={cat.category} className="hover:bg-slate-50">
                            <td className="table-cell font-medium">{cat.category}</td>
                            <td className="table-cell text-right text-slate-600">
                              {formatCurrency(cat.theoreticalCents)}
                            </td>
                            <td className="table-cell text-right text-slate-600">
                              {formatCurrency(cat.actualCents)}
                            </td>
                            <td
                              className={clsx(
                                'table-cell text-right font-semibold',
                                unfavorable ? 'text-danger-600' : 'text-success-600'
                              )}
                            >
                              {unfavorable ? '+' : ''}
                              {formatCurrency(cat.varianceCents)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

        </>
      )}
    </div>
  );
}
